import {extractCatalogHash} from '../../../../utils/imageUtils';
import {extractEditableParams} from './parameterUtils';

/**
 * Parses category image URL to extract hash and initial form values
 * @param {Array} categoryImages - Array of category image objects
 * @param {string|number} primaryCategoryId - The primary category ID
 * @returns {Object} Object containing hash and initial form values
 */
export function parseCategoryImageForConfig(categoryImages, primaryCategoryId) {
  // Find the category image object by category ID
  const categoryImage = categoryImages?.find((img) => {
    const cid = img.category_id || img.primary_category_id || img.id;
    return String(cid) === String(primaryCategoryId);
  });

  if (!categoryImage) {
    return {
      hash: 'default',
      initialValues: {}
    };
  }

  const categoryImageUrl = categoryImage.image_url;

  // Extract hash from the category image URL
  const hash = extractCatalogHash(categoryImageUrl) || 'default';

  // Extract color ID from the URL if present
  const colorMatch = categoryImageUrl?.match(/\/colors\/(\d+)\//);
  let colorId = colorMatch ? colorMatch[1] : null;

  // Fallback to preferred_color_id if URL doesn't contain color
  if (!colorId && categoryImage.preferred_color_id) {
    colorId = String(categoryImage.preferred_color_id);
  }

  // Extract all editable parameters from the URL if available
  const urlParams = categoryImageUrl
    ? extractEditableParams(categoryImageUrl)
    : {};

  // Build initial values with the extracted parameters
  const initialValues = {
    colorId,
    ...urlParams
  };

  return {
    hash,
    initialValues
  };
}

/**
 * Gets the effective hash for lab image generation
 * Prioritizes styleCardImageUrl, falls back to category image
 * @param {string} styleCardImageUrl - Style card image URL
 * @param {Array} categoryImages - Array of category image objects
 * @param {string|number} primaryCategoryId - The primary category ID
 * @returns {string} The hash to use for lab image generation
 */
export function getEffectiveHash(
  styleCardImageUrl,
  categoryImages,
  primaryCategoryId
) {
  // First try to extract from styleCardImageUrl
  if (styleCardImageUrl) {
    const hash = extractCatalogHash(styleCardImageUrl);
    if (hash) {
      return hash;
    }
  }

  // Fallback to category image
  const categoryImage = categoryImages?.find((img) => {
    const cid = img.category_id || img.primary_category_id || img.id;
    return String(cid) === String(primaryCategoryId);
  });

  if (categoryImage?.image_url) {
    const hash = extractCatalogHash(categoryImage.image_url);
    if (hash) {
      return hash;
    }
  }

  return 'default';
}
